import { useState } from 'react';
import { Button } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSwrResumeAddComment } from '@hooks/useSwrApi/endpoints/resume-operations';
import { useSwrUserResumeComments } from '@hooks/useSwrApi/endpoints/user';

interface AddCommentModalContentProps {
  locale?: Locale;
  cvUserId: number;
  userSlug: string;
  onSuccess?: () => void;
  onClose?: () => void;
}

/**
 * @description Component to render the content of the add comment modal.
 */
export const AddCommentModalContent = ({
  locale = 'ro',
  cvUserId,
  userSlug,
  onSuccess,
  onClose,
}: AddCommentModalContentProps) => {
  const { t } = useTranslation('common');
  const [userComment, setUserComment] = useState<string>('');

  const { mutate: mutateComments } = useSwrUserResumeComments({
    locale,
    userSlug,
  });

  const commentAddedSuccessfully = () => {
    void mutateComments();
    if (onSuccess) onSuccess();
    if (onClose) {
      onClose();
    }
  };

  const {
    trigger: addComment,
    isMutating: isAddingComment,
  } = useSwrResumeAddComment(locale, cvUserId, commentAddedSuccessfully);

  return (
    <div className="p-2">
      <textarea
        className="mb-4 w-full rounded-md border p-1 text-base focus:border-primary focus:outline-none"
        value={userComment}
        placeholder={t('cv.action.comment.add')}
        onChange={(e) => {
          setUserComment(e.target.value);
        }}
        rows={6}
      />

      <div className="flex justify-end">
        <Button
          color="secondary"
          rounding="full"
          className="px-8 py-2 font-bold"
          disabled={!userComment || isAddingComment}
          isLoading={isAddingComment}
          onClick={() => {
            void addComment({ comment: userComment });
          }}
        >
          {t('global.action.add')}
        </Button>
      </div>
    </div>
  );
};
